import React, { useState } from 'react';
import FileUpload from './FileUpload';

interface Option {
  text: string;
  is_correct: boolean;
  image_file?: File | null;
  existing_image?: string;
}

interface OptionInputProps {
  option: Option;
  index: number;
  allowMultiple: boolean;
  showImageUploads: boolean;
  onOptionChange: (index: number, field: 'text' | 'is_correct', value: string | boolean) => void;
  onImageChange: (index: number, file: File | null) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  disabled?: boolean;
}

const OptionInput: React.FC<OptionInputProps> = ({
  option,
  index,
  allowMultiple,
  showImageUploads,
  onOptionChange,
  onImageChange,
  onRemove,
  canRemove,
  disabled = false
}) => {
  const [imageExpanded, setImageExpanded] = useState(false);

  const handleImageToggle = () => {
    setImageExpanded(!imageExpanded);
  };

  return (
    <div style={{
      marginBottom: '16px',
      padding: '12px',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '8px',
      background: 'rgba(255,255,255,0.02)'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            margin: 0
          }}>
            Option {index + 1}
            <input
              type={allowMultiple ? "checkbox" : "radio"}
              name={allowMultiple ? `correct-option-${index}` : "correct-option"}
              checked={option.is_correct}
              onChange={() => onOptionChange(index, 'is_correct', !option.is_correct)}
              disabled={disabled}
              style={{ margin: 0 }}
            />
            <span style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.8)'
            }}>
              Correct
            </span>
          </label>
        </div>
        
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            className="form-control"
            value={option.text}
            onChange={(e) => onOptionChange(index, 'text', e.target.value)}
            placeholder={
              option.image_file || option.existing_image 
                ? `Option ${index + 1} (optional - has image)` 
                : `Option ${index + 1}`
            }
            maxLength={200}
            disabled={disabled}
            style={{ 
              flex: 1,
              borderColor: (option.image_file || option.existing_image) && !option.text.trim() 
                ? 'rgba(76, 175, 80, 0.5)' // Green border when image-only
                : undefined
            }}
          />
          
          {showImageUploads && (
            <button
              type="button"
              className="btn btn-small btn-outline"
              onClick={handleImageToggle}
              disabled={disabled}
              title="Add image to this option"
              style={{
                background: (imageExpanded || option.image_file || option.existing_image) 
                  ? 'rgba(33, 150, 243, 0.2)' 
                  : 'rgba(33, 150, 243, 0.1)',
                border: (imageExpanded || option.image_file || option.existing_image)
                  ? '1px solid rgba(33, 150, 243, 0.5)'
                  : '1px solid rgba(33, 150, 243, 0.3)',
                color: (imageExpanded || option.image_file || option.existing_image)
                  ? '#1976d2'
                  : '#2196f3',
                padding: '6px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              🖼️
            </button>
          )}
          
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="btn btn-danger btn-small"
            disabled={!canRemove || disabled}
            title="Remove option"
          >
            Delete
          </button>
        </div>
      </div>
      
      {showImageUploads && (imageExpanded || option.image_file || option.existing_image) && (
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <FileUpload
            label=""
            accept="image/*"
            file={option.image_file || null}
            existingFile={option.existing_image}
            onFileChange={(file) => onImageChange(index, file)}
            placeholder="Choose image for this option..."
            disabled={disabled}
            maxPreviewHeight={120}
            className="form-control"
          />
        </div>
      )}
    </div>
  );
};

export default OptionInput;
